import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, QrCode, XCircle } from 'lucide-react';
import { Card, StatusBadge } from '../../shared/ui';
import { OrderStatus, formatCurrency, restaurantPayout, timeAgo } from '../../shared/models';
import { restaurantAcceptOrder, markOrderPreparing, markOrderReady } from '../../shared/services/orderService';
import RejectOrderSheet from './RejectOrderSheet';

// Mirrors RestaurantOrderRow + OrderActionButtons: id + status badge, item
// count + total, inline Accept/Reject for Pending, Start Prep for Confirmed,
// Mark Ready for Preparing. Tap card → open order detail.
export default function OrderCard({ order, onOpen }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(''); // 'accept' | 'prepare' | 'ready'
  const [error, setError] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);

  const run = (key, fn) => async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(key);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err.message || 'Action failed. Please try again.');
    } finally {
      setBusy('');
    }
  };

  const openReject = (e) => {
    e.stopPropagation();
    setRejectOpen(true);
  };

  const itemCount = order.orderItems.reduce((sum, it) => sum + (it.quantity || 1), 0);

  return (
    <>
      <Card className="p-3">
        <div onClick={onOpen} role="button" tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onOpen?.()}
          className="flex items-center gap-3 cursor-pointer">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">#{String(order.id).slice(0, 8)}</span>
              <StatusBadge status={order.status} />
            </div>
            <div className="flex items-center gap-2 mt-1.5 text-xs">
              <span className="text-gray-500">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
              <span className="font-semibold text-gray-900">{formatCurrency(restaurantPayout(order))}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-400 truncate">
              <span className="truncate">{order.customerName || 'Customer'}</span>
              {order.createdAt && <span className="shrink-0">• {timeAgo(order.createdAt)}</span>}
            </div>
          </div>

          {/* Pending → Reject / Accept (iOS red xmark + green checkmark pills) */}
          {order.status === OrderStatus.PENDING && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={openReject}
                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-semibold active:opacity-80"
              >
                <XCircle size={14} />
                Reject
              </button>
              <button
                onClick={run('accept', () => restaurantAcceptOrder(order))}
                disabled={!!busy}
                className="flex items-center gap-1 px-3 py-2 rounded-lg bg-green-500 text-white text-xs font-semibold active:opacity-80 disabled:opacity-60"
              >
                {busy === 'accept' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Accept
              </button>
            </div>
          )}

          {/* Confirmed → Start Prep (blue) / Preparing → Mark Ready (green) */}
          {order.status === OrderStatus.CONFIRMED && (
            <button
              onClick={run('prepare', () => markOrderPreparing(order))}
              disabled={!!busy}
              className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-500 text-white text-xs font-semibold active:opacity-80 disabled:opacity-60"
            >
              {busy === 'prepare' && <Loader2 size={14} className="animate-spin" />}
              Start Prep
            </button>
          )}
          {order.status === OrderStatus.PREPARING && (
            <button
              onClick={run('ready', () => markOrderReady(order))}
              disabled={!!busy}
              className="shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-green-500 text-white text-xs font-semibold active:opacity-80 disabled:opacity-60"
            >
              {busy === 'ready' && <Loader2 size={14} className="animate-spin" />}
              Mark Ready
            </button>
          )}

          {/* Ready → tiny QR shortcut (no printed receipt needed) */}
          {order.status === OrderStatus.READY && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/business/orders/${order.id}/receipt`); }}
              className="shrink-0 flex flex-col items-center gap-0.5 text-blue-600 active:opacity-80"
            >
              <QrCode size={16} />
              <span className="text-[10px] font-medium">Show QR</span>
            </button>
          )}
        </div>
        {error && <p className="text-[11px] font-medium text-red-500 mt-2">{error}</p>}
      </Card>

      <RejectOrderSheet order={order} open={rejectOpen} onClose={() => setRejectOpen(false)} />
    </>
  );
}
